import axios from "axios";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Briefcase,
  MapPin,
  Users,
  ChevronRight,
  Trash2,
  Loader2,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import CompanyCareerLinksPanel from "@/components/CompanyCareerLinksPanel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useDocumentTitle from "@/hooks/useDocumentTitle";

// "Em aberto" agrupa vagas ativas e pausadas; "Encerrada" é o status final.
const OPEN_STATUSES = ["ativa", "pausada"];

const STATUS_FILTERS = [
  { value: "todas", label: "Todas as vagas" },
  { value: "aberta", label: "Em aberto" },
  { value: "encerrada", label: "Encerrada" },
];

const JobsListPage = () => {
  const { can } = useAuth();
  const canDelete = can("jobs", "delete");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null); // job pending confirmation
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todas");

  useDocumentTitle(
    jobs.length ? `${jobs.length} vagas` : "Vagas",
    { ready: !loading },
  );

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/jobs`);
        setJobs(res.data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredJobs = jobs.filter((job) => {
    const term = search.trim().toLowerCase();
    // Código aceita variações: "V014", "v14" ou só "14" encontram o mesmo job_code.
    const termDigits = term.replace(/^v/, "").replace(/^0+(?=\d)/, "");
    const codeDigits = (job.job_code || "")
      .toLowerCase()
      .replace(/^v/, "")
      .replace(/^0+(?=\d)/, "");
    const matchesSearch =
      job.title?.toLowerCase().includes(term) ||
      job.job_code?.toLowerCase().includes(term) ||
      (termDigits !== "" && codeDigits === termDigits);
    const matchesStatus =
      statusFilter === "todas" ||
      (statusFilter === "aberta" && OPEN_STATUSES.includes(job.status)) ||
      (statusFilter === "encerrada" && job.status === "encerrada");
    return matchesSearch && matchesStatus;
  });

  const hasActiveFilters = search.trim() !== "" || statusFilter !== "todas";

  const handleConfirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/jobs/${deleting.id}`);
      setJobs((prev) => prev.filter((j) => j.id !== deleting.id));
      toast.success(`Vaga "${deleting.title}" excluída`);
      setDeleting(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir vaga");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="jobs-list-page" className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
            Vagas
          </div>
          <h1 className="heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">
            Suas vagas
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Gerencie vagas, faça upload de currículos e acompanhe candidatos.
          </p>
        </div>
        <Link
          to="/vagas/nova"
          data-testid="create-job-button"
          className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nova vaga
        </Link>
      </div>

      {can("registry", "organizations") && <CompanyCareerLinksPanel />}

      {!loading && jobs.length > 0 && (
        <div
          data-testid="jobs-filter-bar"
          className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row gap-3 sm:items-center"
        >
          <div className="flex items-center gap-2 text-slate-500 shrink-0">
            <SlidersHorizontal className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wide">
              Filtrar vagas
            </span>
          </div>
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="jobs-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar vaga por nome ou código..."
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              data-testid="jobs-status-filter"
              className="sm:w-48 shrink-0"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Carregando vagas...</div>
      ) : jobs.length === 0 ? (
        <div
          data-testid="jobs-empty-state"
          className="bg-white border border-dashed border-slate-300 rounded-xl p-16 text-center"
        >
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-5 h-5 text-slate-600" />
          </div>
          <h3 className="heading text-lg font-semibold text-slate-900 mb-1">
            Nenhuma vaga criada ainda
          </h3>
          <p className="text-sm text-slate-600 mb-5 max-w-sm mx-auto">
            Crie sua primeira vaga descrevendo as características desejadas do candidato ideal.
          </p>
          <Link
            to="/vagas/nova"
            data-testid="empty-create-job-button"
            className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Criar vaga
          </Link>
        </div>
      ) : filteredJobs.length === 0 ? (
        <div
          data-testid="jobs-no-results"
          className="bg-white border border-dashed border-slate-300 rounded-xl p-16 text-center"
        >
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-5 h-5 text-slate-600" />
          </div>
          <h3 className="heading text-lg font-semibold text-slate-900 mb-1">
            Nenhuma vaga encontrada
          </h3>
          <p className="text-sm text-slate-600 mb-5 max-w-sm mx-auto">
            Ajuste a busca ou o filtro de status para ver outras vagas.
          </p>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatusFilter("todas");
              }}
              className="text-sm font-medium text-brand-blue hover:underline"
            >
              Limpar filtros
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filteredJobs.map((job) => (
            <div
              key={job.id}
              data-testid={`job-card-${job.id}`}
              className="group relative bg-white border border-slate-200 rounded-lg hover:border-slate-400 transition-colors"
            >
              <Link
                to={`/vagas/${job.id}`}
                className="block p-5 pb-3"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={job.status} />
                    {job.job_code && (
                      <span className="text-xs font-mono font-semibold text-slate-500">
                        {job.job_code}
                      </span>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition-colors" />
                </div>
                <h3 className="heading text-lg font-semibold text-slate-900 leading-snug mb-1 line-clamp-2">
                  {job.title}
                </h3>
                {job.department && (
                  <p className="text-sm text-slate-600 mb-4">{job.department}</p>
                )}
                <p className="text-sm text-slate-600 line-clamp-3 mb-4">
                  {job.description}
                </p>
                <div className="flex flex-wrap gap-3 text-xs text-slate-500 pt-3 border-t border-slate-100">
                  {job.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {job.location}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {job.candidates_count} candidato{job.candidates_count !== 1 ? "s" : ""}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Briefcase className="w-3.5 h-3.5" />
                    {job.employment_type}
                  </span>
                </div>
              </Link>

              {/* Footer action bar — always visible, separated from the clickable area */}
              {canDelete && (
                <div className="px-5 py-3 border-t border-slate-100 flex justify-end">
                  <button
                    type="button"
                    data-testid={`delete-job-${job.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDeleting(job);
                    }}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 hover:text-white hover:bg-rose-600 border border-rose-200 hover:border-rose-600 rounded-md px-2.5 py-1.5 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Excluir vaga
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!deleting}
        onOpenChange={(open) => !open && !busy && setDeleting(null)}
      >
        <AlertDialogContent data-testid="delete-job-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta vaga?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Você está prestes a excluir{" "}
                <strong className="text-slate-900">{deleting?.title}</strong>.
              </span>
              {(deleting?.candidates_count ?? 0) > 0 && (
                <span className="block bg-rose-50 border border-rose-200 text-rose-800 rounded-md px-3 py-2 text-xs">
                  ⚠️ Esta vaga tem{" "}
                  <strong>{deleting.candidates_count}</strong> candidato
                  {deleting.candidates_count !== 1 ? "s" : ""} cadastrado
                  {deleting.candidates_count !== 1 ? "s" : ""}. Todos eles e
                  seus currículos serão permanentemente apagados também.
                </span>
              )}
              <span className="block text-xs">Esta ação não pode ser desfeita.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-job-cancel"
              disabled={busy}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-job-confirm"
              disabled={busy}
              onClick={handleConfirmDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Excluindo…
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir definitivamente
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JobsListPage;
